import { useCallback, useRef } from 'react';
import { useGamesState } from '../State';

export enum NotificationType {
  Error,
  Info,
}

let notificationId = 1;

export interface Notification {
  id: number;
  type: NotificationType;
  message: string;
  duration?: number;
}

export function useNotifications() {
  const { notifications, setNotifications } = useGamesState();

  // The state setter takes the whole list rather than an update function, so a
  // callback that closed over `notifications` would write back a list from
  // whenever it was created: a message queued behind another would be dropped,
  // and the one that expired would take the wrong entry with it. Reading the
  // latest list through a ref keeps every message, in the order it was raised.
  const latest = useRef(notifications);
  latest.current = notifications;

  const error = useCallback(
    (message: string | string[], duration?: number) => {
      const newMessages = typeof message === 'string' ? [message] : message;
      const toCreate = newMessages.map((c) => ({
        id: notificationId++,
        type: NotificationType.Error,
        message: c,
        duration,
      }));
      setNotifications([...latest.current, ...toCreate]);
    },
    [setNotifications],
  );

  const info = useCallback(
    (message: string | string[], duration?: number) => {
      const newMessages = typeof message === 'string' ? [message] : message;
      const toCreate = newMessages.map((c) => ({
        id: notificationId++,
        type: NotificationType.Info,
        message: c,
        duration,
      }));
      setNotifications([...latest.current, ...toCreate]);
    },
    [setNotifications],
  );

  const remove = useCallback(
    (id: number) => {
      setNotifications(latest.current.filter((n) => n.id !== id));
    },
    [setNotifications],
  );

  return {
    error,
    info,
    remove,
    notifications,
  };
}
